import React, { useContext, useState } from "react";
import styled from "styled-components";
import {
  Button,
  Icon,
  IconName,
  TextInput,
  Theme,
  colors,
} from "@literati/public-ui-shared-react";
import { Form, Formik } from "formik";
import { isAPIError } from "lib/api/base";
import { submitFormik } from "utils/forms";
import DLSErrorMessage from "components/DLS/DLSErrorMessage";
import {
  CouponSubmit,
  CouponValues,
} from "components/Payment/paymentFormTypes";
import { CheckoutContext } from "contexts/CheckoutProvider";
import { isUserEntryCouponIncompatiblePlanSlug } from "components/PlanSelection/planSelectionTypes";

export const FORM_ID = "payment-form-coupon";

const CLASS_BASE = "payment-form-coupon";
export const PaymentFormCouponClassName = {
  BASE: CLASS_BASE,
  TEXT_INPUT: `${CLASS_BASE}-text-input`,
} as const;

const StyledButton = styled(Button)`
  position: absolute;
  right: 0.5rem;
  top: 0;
  width: 2rem;
`;

const Toggle = styled(Button)`
  font-size: 0.875rem;
  color: ${colors.periwinkleShade1};

  &:hover {
    text-decoration: none;
    color: ${colors.periwinkleShade2};
  }
`;

export interface PaymentFormCouponProps {
  className?: string;
  initialShowInput?: boolean;
  onSubmit?: CouponSubmit;
  onSuccess?: (res: unknown) => void;
}

export const PaymentFormCoupon = ({
  className = "",
  initialShowInput = false,
  onSubmit,
  onSuccess,
}: PaymentFormCouponProps) => {
  const cls = PaymentFormCouponClassName;

  const { addPromoCode, getCheckoutSession, session } =
    useContext(CheckoutContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showInput, setShowInput] = useState(initialShowInput);

  const handleSubmit: CouponSubmit = async (values, formikProps) => {
    return await submitFormik({
      onSubmit: async () => {
        setLoading(true);
        const res = await addPromoCode?.(values.coupon);
        if (isAPIError(res)) {
          setError(res?.error?.message || null);
        } else {
          getCheckoutSession?.();
        }
        setLoading(false);
        onSubmit?.(values, formikProps);
        return res;
      },
      onSuccess,
      formikProps,
    });
  };

  const selectedPlanSlug = session?.selectedPlan;
  const isPlanIncompatibleForUserCouponEntry =
    isUserEntryCouponIncompatiblePlanSlug(selectedPlanSlug);
  if (isPlanIncompatibleForUserCouponEntry) {
    return null;
  }

  return (
    <Theme>
      {showInput ? (
        <Formik<CouponValues>
          initialValues={{ coupon: "" }}
          onSubmit={handleSubmit}
        >
          {({ handleChange }) => {
            return (
              <Form
                className={`${cls.BASE} ${className}`}
                css="position: relative"
                id={FORM_ID}
              >
                <TextInput
                  className={cls.TEXT_INPUT}
                  label="Enter Coupon Code"
                  name="coupon"
                  onChange={(e) => {
                    handleChange(e);
                    setError(null);
                  }}
                  placeholder="Enter Coupon Code"
                />
                <StyledButton
                  disabled={loading}
                  form={FORM_ID}
                  type="submit"
                  variant="unstyled"
                >
                  <Icon name={IconName.ARROW} />
                </StyledButton>
                {error && (
                  <DLSErrorMessage css="padding-left: 0" message={error} />
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        <Toggle
          className={`${cls.BASE} ${className}`}
          onClick={() => setShowInput(true)}
          variant="link"
        >
          Enter Coupon Code +
        </Toggle>
      )}
    </Theme>
  );
};

export default PaymentFormCoupon;
